import type { ReactNode } from 'react'
import type {
  PublicResearchMatchDetailMetric,
  WorldcupResearchQuery,
} from '~/features/worldcup-research/types'
import type { ImplementedLocale } from '~/lib/i18n/shared'
import { Button, Card, Surface } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { Link, Outlet, useLocation } from '@tanstack/react-router'
import {
  BarChart3,
  GitCompare,
  Search,
  ShieldCheck,
  Trophy,
} from 'lucide-react'
import * as React from 'react'
import { useLocale, useTranslations } from 'use-intl'
import { ProductShellLayout } from '~/components/layout/product-shell-layout'
import { OpportunityMonitoringConsole } from '~/features/opportunity-monitoring/components'
import {
  initialPublicResearchCoverageFilters,
  publicResearchCoverageQueryOptions,
} from '~/features/public-research/query-options'
import { formatCompetitionName } from '~/lib/i18n/messages/domain/competitions'
import { formatTeamName } from '~/lib/i18n/messages/domain/teams'
import { researchNavItems } from '~/lib/navigation/routes'
import { classNames } from './utils'

export { EvidenceExplorer } from './evidence-explorer'
export { GuidedWorldcupResearch } from './guided-worldcup-research'
export { HeadToHeadResearchView } from './head-to-head'
export { MatchDetailResearchView } from './match-detail'
export { NationalTeamResearchProfile } from './team-profile'

const popularWorldCupQualifierTeams = [
  'Argentina',
  'France',
  'Brazil',
  'Spain',
  'England',
  'Portugal',
  'Germany',
  'Netherlands',
  'Belgium',
  'Uruguay',
  'Colombia',
  'Croatia',
] as const
const sampleQueries = [
  { key: 'argentina2022', team: 'Argentina', competitionCode: 'world_cup', competitionName: 'FIFA World Cup', season: 2022 },
  { key: 'france2018', team: 'France', competitionCode: 'world_cup', competitionName: 'FIFA World Cup', season: 2018 },
  { key: 'spain2024', team: 'Spain', competitionCode: 'euro', competitionName: 'Euro Championship', season: 2024 },
] as const
const headToHeadPairs = [
  { home: 'Argentina', away: 'France' },
  { home: 'France', away: 'Brazil' },
  { home: 'Spain', away: 'Germany' },
] as const
const competitionCards = [
  { code: 'world_cup', label: 'World Cup', season: 2022 },
  { code: 'euro', label: 'Euro', season: 2024 },
  { code: 'copa_america', label: 'Copa America', season: 2024 },
  { code: 'world_cup_qualifier', label: 'World Cup Qualifiers', season: 2024 },
] as const

export function formatValue(value: unknown) {
  if (value === null || value === undefined || value === '')
    return '-'
  if (typeof value === 'number')
    return Number.isInteger(value) ? String(value) : value.toFixed(2)
  return String(value)
}

export function formatMetricValue(metric: PublicResearchMatchDetailMetric, value: unknown) {
  const formatted = formatValue(value)
  if (formatted === '-')
    return formatted
  const isPercentMetric = ['ballPossession', 'passAccuracy'].includes(metric.key)
  if (isPercentMetric && !formatted.endsWith('%')) {
    return `${formatted}%`
  }
  return formatted
}

export function compactJson(value: unknown) {
  return JSON.stringify(value, null, 2)
}

export interface EvidenceTableRow {
  id: string
  team: string
  opponent: string
  competitionName: string
  season: string | number
  goals: number | null
  opponentGoals: number | null
  shotsOnTarget: number | null
  corners: number | null
}

export function isEvidenceTableRow(row: unknown): row is EvidenceTableRow {
  if (typeof row !== 'object' || row === null)
    return false
  const record = row as Record<string, unknown>
  return typeof record.id === 'string'
    && typeof record.team === 'string'
    && typeof record.opponent === 'string'
    && typeof record.competitionName === 'string'
    && (typeof record.season === 'string' || typeof record.season === 'number')
}

export function downloadCsv(filename: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function uniqueValues(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter(Boolean))) as string[]
}

export function readableTeamValue(value: string) {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export function formatProviderName(provider: string) {
  if (provider === 'api_football')
    return 'API-Football'
  if (provider === 'statsbomb')
    return 'StatsBomb'
  return provider
}

export function metricShare(value: unknown, max: number) {
  if (typeof value !== 'number' || value <= 0 || max <= 0)
    return '0%'
  return `${Math.max(6, Math.round((value / max) * 100))}%`
}

export function matchDetailPath(matchId: string, locale: ImplementedLocale) {
  const cleanMatchId = matchId.match(/\d+$/)?.[0] ?? matchId
  return locale === 'zh-CN'
    ? { to: '/zh-CN/research/matches/$matchId' as const, params: { matchId: cleanMatchId } }
    : { to: '/research/matches/$matchId' as const, params: { matchId: cleanMatchId } }
}

export function researchWorldcupPath(locale: ImplementedLocale) {
  return locale === 'zh-CN' ? '/zh-CN/research/worldcup' : '/research/worldcup'
}

function researchHeadToHeadPath(locale: ImplementedLocale) {
  return locale === 'zh-CN' ? '/zh-CN/research/head-to-head' : '/research/head-to-head'
}

function researchEvidencePath(locale: ImplementedLocale) {
  return locale === 'zh-CN' ? '/zh-CN/research/evidence' : '/research/evidence'
}

export function translateOptional(t: ReturnType<typeof useTranslations>, key: string, fallback: string) {
  try {
    return t(key as any)
  }
  catch {
    return fallback
  }
}

export function metricDisplayName(metricKey: string, fallback: string, t: ReturnType<typeof useTranslations>) {
  return translateOptional(t, `fieldLabels.${metricKey}`, fallback)
}

export function localizedDefinitionNote(metric: PublicResearchMatchDetailMetric, locale: ImplementedLocale, t: ReturnType<typeof useTranslations>) {
  const fallback = locale === 'zh-CN' ? t('definitionNotes.fallback') : metric.definitionNote
  return translateOptional(t, `definitionNotes.${metric.key}`, fallback)
}

export function numberValue(value: unknown): number | null {
  if (typeof value === 'number')
    return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace('%', ''))
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export function ResearchWorkspaceLayout() {
  const location = useLocation()
  const isMatchDetail = location.pathname.includes('/research/matches/')

  return (
    <ProductShellLayout>
      <ResearchSectionNav isCompact={isMatchDetail} />
      <Outlet />
    </ProductShellLayout>
  )
}

function ResearchSectionNav({ isCompact = false }: { isCompact?: boolean }) {
  const t = useTranslations('ResearchWorkspace')
  const locale = useLocale() as ImplementedLocale
  const location = useLocation()
  const navItems = researchNavItems.map(item => ({
    ...item,
    label: t(`nav.${item.labelKey}`),
  }))

  return (
    <section className={classNames('rounded-lg border border-divider bg-default-50/60 p-3', isCompact ? 'mb-3' : 'mb-6')} aria-labelledby="research-section-title">
      {!isCompact && (
        <div className="mb-3 flex flex-col gap-1 px-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 id="research-section-title" className="text-lg font-black tracking-normal">{t('title')}</h1>
            <p className="text-sm text-muted">{t('subtitle')}</p>
          </div>
        </div>
      )}
      {isCompact && <h1 id="research-section-title" className="sr-only">{t('title')}</h1>}
      <nav className="flex gap-2 overflow-x-auto" aria-label={t('nav.ariaLabel')}>
        {navItems.map((item) => {
          const Icon = item.icon
          const localizedTo = locale === 'zh-CN'
            ? `/zh-CN${item.to}`
            : item.to
          const isActive = location.pathname === item.to || location.pathname === localizedTo
          return (
            <Link
              key={item.to}
              to={localizedTo as any}
              className={classNames(
                'inline-flex min-h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold whitespace-nowrap',
                isActive ? 'bg-primary text-primary-foreground' : 'text-muted hover:bg-default-100 hover:text-foreground',
              )}
            >
              <Icon className="size-4" />
              {item.label}
            </Link>
          )
        })}
      </nav>
    </section>
  )
}

export function ResearchDiscoveryHome() {
  const t = useTranslations('ResearchWorkspace.discovery')
  const locale = useLocale() as ImplementedLocale
  const [starterMode, setStarterMode] = React.useState<'team' | 'headToHead' | 'competition'>('team')
  const coverageQuery = useQuery(publicResearchCoverageQueryOptions({
    ...initialPublicResearchCoverageFilters,
    coverageLimit: 8,
    gapLimit: 4,
  }))
  const coverageRows = coverageQuery.data?.rows ?? []
  const starterTabs = [
    { key: 'team' as const, icon: <Search className="size-4" />, label: t('starter.tabs.team') },
    { key: 'headToHead' as const, icon: <GitCompare className="size-4" />, label: t('starter.tabs.headToHead') },
    { key: 'competition' as const, icon: <Trophy className="size-4" />, label: t('starter.tabs.competition') },
  ]
  const teamStarterItems = popularWorldCupQualifierTeams.map(team => ({
    key: `team:${team}`,
    title: formatTeamName(team, locale),
    body: undefined,
    to: researchWorldcupPath(locale),
    search: { team },
  }))
  const headToHeadStarterItems = headToHeadPairs.map(pair => ({
    key: `h2h:${pair.home}:${pair.away}`,
    title: `${formatTeamName(pair.home, locale)} vs ${formatTeamName(pair.away, locale)}`,
    body: t('starter.headToHeadItemBody'),
    to: researchHeadToHeadPath(locale),
    search: { team: pair.home, opponent: pair.away },
  }))
  const competitionStarterItems = competitionCards.map(card => ({
    key: `competition:${card.code}:${card.season}`,
    title: formatCompetitionName(card.code, locale),
    body: t('competitionScope', { season: card.season }),
    to: researchWorldcupPath(locale),
    search: { competitionCode: card.code, season: card.season },
  }))
  const starterItems = starterMode === 'team'
    ? teamStarterItems
    : starterMode === 'headToHead'
      ? headToHeadStarterItems
      : competitionStarterItems

  return (
    <div className="space-y-5">
      <section className="grid gap-4 lg:grid-cols-[0.85fr_1.15fr] lg:items-stretch">
        <Surface className="rounded-lg border border-divider p-6" variant="secondary">
          <div className="mb-3 inline-flex items-center gap-2 rounded-md bg-success/10 px-3 py-1 text-xs font-semibold text-success">
            <ShieldCheck className="size-4" />
            {t('eyebrow')}
          </div>
          <h1 className="max-w-3xl text-3xl font-black tracking-normal md:text-5xl">{t('title')}</h1>
          <p className="mt-4 max-w-3xl text-base text-muted md:text-lg">{t('subtitle')}</p>
          <p className="mt-5 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm font-semibold text-foreground">
            {t('starterHint')}
          </p>
        </Surface>
        <Surface className="rounded-lg border border-divider p-5" variant="secondary">
          <div className="mb-4">
            <h2 className="text-lg font-bold">{t('starter.title')}</h2>
            <p className="mt-1 text-sm text-muted">{t('starter.subtitle')}</p>
          </div>
          <div className="grid gap-2 rounded-lg border border-divider bg-background/60 p-1 sm:grid-cols-3">
            {starterTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                className={classNames(
                  'inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-3 text-sm font-bold transition',
                  starterMode === tab.key ? 'bg-primary text-primary-foreground' : 'text-muted hover:bg-default-100 hover:text-foreground',
                )}
                onClick={() => setStarterMode(tab.key)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
          <div className={classNames('mt-4 grid gap-2', starterMode === 'team' ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
            {starterItems.map(item => (
              <StarterLinkCard key={item.key} to={item.to} search={item.search} title={item.title} body={item.body} />
            ))}
          </div>
        </Surface>
      </section>

      <section className="grid gap-4 xl:grid-cols-[1fr_0.9fr]">
        <Card className="p-5" variant="secondary">
          <Card.Header>
            <div className="flex items-center gap-2">
              <Search className="size-5 text-primary" />
              <h2 className="text-lg font-bold">{t('sampleQueries.title')}</h2>
            </div>
          </Card.Header>
          <Card.Content className="grid gap-3 md:grid-cols-3">
            {sampleQueries.map(query => (
              <Link
                key={query.key}
                to={researchWorldcupPath(locale) as any}
                search={{
                  team: query.team,
                  competitionCode: query.competitionCode,
                  competitionName: query.competitionName,
                  season: query.season,
                } as any}
                className="rounded-md border border-divider p-4 hover:bg-default-100"
              >
                <div className="font-semibold">{t(`sampleQueries.items.${query.key}.title`)}</div>
                <div className="mt-1 text-sm text-muted">{t(`sampleQueries.items.${query.key}.body`)}</div>
              </Link>
            ))}
          </Card.Content>
        </Card>

        <Card className="p-5" variant="secondary">
          <Card.Header>
            <div className="flex items-center gap-2">
              <BarChart3 className="size-5 text-primary" />
              <h2 className="text-lg font-bold">{t('coverageTitle')}</h2>
            </div>
          </Card.Header>
          <Card.Content className="grid gap-3">
            {coverageQuery.isPending && <StateText>{t('loading')}</StateText>}
            {coverageQuery.isError && <StateText tone="danger">{t('coverageError')}</StateText>}
            {coverageRows.slice(0, 4).map((row: any) => (
              <div key={`${row.competitionCode}:${row.season}:${row.competitionName}`} className="rounded-md border border-divider p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold">{formatCompetitionName(row.competitionName, locale)}</div>
                  <div className="text-sm text-muted">{row.season}</div>
                </div>
                <div className="mt-2 text-xs text-muted">
                  {t('coverageRows', { matches: row.researchMatchCount, rows: row.teamMatchStatRowCount })}
                </div>
              </div>
            ))}
          </Card.Content>
        </Card>
      </section>

      <section className="rounded-lg border border-divider bg-default-50/70 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-bold">{t('advancedToolsTitle')}</h2>
            <p className="mt-1 text-sm text-muted">{t('advancedToolsBody')}</p>
          </div>
          <Link to={researchEvidencePath(locale) as any} search={{} as any}>
            <Button variant="outline" size="sm">{t('openEvidence')}</Button>
          </Link>
        </div>
      </section>
    </div>
  )
}

export function SummaryMetric({ label, value }: { label: string, value: number | string }) {
  return (
    <div className="rounded-md border border-divider bg-background/60 p-3">
      <div className="text-xs font-semibold uppercase text-muted">{label}</div>
      <div className="mt-1 text-xl font-black">{formatValue(value)}</div>
    </div>
  )
}

function StarterLinkCard({ to, search, title, body }: { to: string, search: Record<string, unknown>, title: string, body?: string }) {
  return (
    <Link to={to as any} search={search as any} className="block">
      <div className="flex h-full min-h-12 items-center rounded-md border border-divider bg-background/60 p-3 transition hover:border-primary hover:bg-default-100">
        <h3 className="text-sm font-bold">{title}</h3>
        {body && <p className="mt-1 text-sm text-muted">{body}</p>}
      </div>
    </Link>
  )
}

export function StateText({ children, tone = 'muted' }: { children: ReactNode, tone?: 'muted' | 'danger' | 'warning' }) {
  return (
    <div className={classNames(
      'text-sm',
      tone === 'danger' && 'text-danger',
      tone === 'warning' && 'text-warning',
      tone === 'muted' && 'text-muted',
    )}
    >
      {children}
    </div>
  )
}

export function isUnauthorizedResearchError(error: unknown) {
  if (!(error instanceof Error))
    return false
  return /unauthorized|401|sign in|login/i.test(error.message)
}

export function ResearchStateNotice({
  title,
  body,
  tone = 'muted',
}: {
  title: string
  body: string
  tone?: 'muted' | 'danger' | 'warning'
}) {
  return (
    <Card
      className={classNames(
        'p-5',
        tone === 'danger' && 'border-danger/40',
        tone === 'warning' && 'border-warning/40',
      )}
      variant="secondary"
    >
      <div className={classNames(
        'text-sm font-bold',
        tone === 'danger' && 'text-danger',
        tone === 'warning' && 'text-warning',
      )}
      >
        {title}
      </div>
      <p className="mt-2 text-sm text-muted">{body}</p>
    </Card>
  )
}

export function userVisibleWarnings(warnings: string[]) {
  return warnings.filter(warning => warning !== 'single_provider')
}

export function SourceWarningList({ title, warnings }: { title: string, warnings: string[] }) {
  const visibleWarnings = userVisibleWarnings(warnings)
  if (visibleWarnings.length === 0)
    return null

  return (
    <ResearchStateNotice
      title={title}
      body={visibleWarnings.join(', ')}
      tone="warning"
    />
  )
}
export function ResearchOpportunityMonitor() {
  const t = useTranslations('ResearchWorkspace.opportunities')
  return (
    <div className="space-y-4">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} subtitle={t('subtitle')} />
      <OpportunityMonitoringConsole />
    </div>
  )
}

export function PageHeader({ eyebrow, title, subtitle }: { eyebrow: string, title: string, subtitle: string }) {
  return (
    <header>
      <div className="text-sm font-semibold uppercase tracking-normal text-primary">{eyebrow}</div>
      <h1 className="mt-2 text-3xl font-black tracking-normal md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-sm text-muted md:text-base">{subtitle}</p>
    </header>
  )
}

export function TextInput({ label, value, onChange, list, inputMode }: { label: string, value: string, onChange: (value: string) => void, list?: string, inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'] }) {
  return (
    <label className="grid gap-1 text-xs font-semibold uppercase text-muted">
      {label}
      <input
        className="rounded-md border border-divider bg-background px-3 py-2 text-sm normal-case text-foreground"
        value={value}
        list={list}
        inputMode={inputMode}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  )
}

export type SelectOption = string | { value: string, label: string }

export function SelectInput({ label, value, options, onChange }: { label: string, value: string, options: SelectOption[], onChange: (value: string) => void }) {
  const fieldId = React.useId()

  return (
    <label className="grid gap-1 text-xs font-semibold uppercase text-muted">
      {label}
      <select
        id={fieldId}
        name={fieldId}
        className="rounded-md border border-divider bg-background px-3 py-2 text-sm normal-case text-foreground"
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        {options.map((option) => {
          const optionValue = typeof option === 'string' ? option : option.value
          const optionLabel = typeof option === 'string' ? option : option.label
          return <option key={optionValue || 'default'} value={optionValue}>{optionLabel || '-'}</option>
        })}
      </select>
    </label>
  )
}

export function AppliedFilters({
  filters,
  competitionOptions,
}: {
  filters: WorldcupResearchQuery
  competitionOptions: SelectOption[]
}) {
  const t = useTranslations('ResearchWorkspace.worldcup')
  const locale = useLocale() as ImplementedLocale
  const labelFor = (options: SelectOption[], value: string | undefined) => {
    if (!value)
      return ''
    const found = options.find(option => (typeof option === 'string' ? option : option.value) === value)
    return typeof found === 'string' ? found : found?.label ?? value
  }
  const competitionValue = filters.competitionCodes?.length
    ? filters.competitionCodes.map(code => labelFor(competitionOptions, code)).join(', ')
    : labelFor(competitionOptions, filters.competitionCode)
  const yearRangeValue = filters.fromYear || filters.toYear
    ? `${filters.fromYear ?? t('allSeasons')} - ${filters.toYear ?? t('presentYear')}`
    : undefined
  const entries = [
    { key: 'team', label: t('teamLabel'), value: formatTeamName(filters.team, locale) },
    { key: 'opponent', label: t('opponentLabel'), value: formatTeamName(filters.opponent, locale) },
    { key: 'competitionCode', label: t('competitionCodeLabel'), value: competitionValue },
    { key: 'competitionName', label: t('competitionLabel'), value: formatCompetitionName(filters.competitionName, locale) },
    { key: 'yearRange', label: t('timeRangeLabel'), value: yearRangeValue },
    { key: 'season', label: t('editionLabel'), value: filters.season },
  ].filter(item => item.value !== undefined && item.value !== '')
  return (
    <div className="flex flex-wrap gap-2 text-xs" aria-label={t('appliedFilters')}>
      <span className="font-semibold uppercase text-muted">{t('appliedFilters')}</span>
      {entries.map(item => (
        <span key={item.key} className="rounded-md bg-default-100 px-2 py-1">
          {item.label}
          :
          {' '}
          {formatValue(item.value)}
        </span>
      ))}
    </div>
  )
}

export function KeyValue({ label, value }: { label: string, value: unknown }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted">{label}</div>
      <div className="mt-1 text-sm font-semibold">{formatValue(value)}</div>
    </div>
  )
}

export function EvidenceList({ title, values, formatItem = value => value }: {
  title: string
  values: string[]
  formatItem?: (value: string) => string
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted">{title}</div>
      {values.length === 0
        ? <div className="mt-1 text-sm text-muted">-</div>
        : (
            <div className="mt-2 flex flex-wrap gap-2">
              {values.map(value => <span key={value} className="rounded-md bg-default-100 px-2 py-1 text-xs">{formatItem(value)}</span>)}
            </div>
          )}
    </div>
  )
}
